/**
 * Транспорт до сервиса.
 *
 * Формат ответа один на весь API: `{ error: [], results }`. Разбирается он
 * здесь, в одном месте, — иначе каждый вызов начинал бы с проверки `error`
 * и рано или поздно кто-то её забудет.
 */

export type ApiErrorItem = { code: string; message: string; field?: string };

export class ApiFailure extends Error {
  readonly status: number;
  readonly items: ApiErrorItem[];
  /** Номер запроса (`X-Request-ID`): по нему ошибка браузера находит ошибку сервера. */
  readonly requestId: string | undefined;

  constructor(status: number, items: ApiErrorItem[], requestId?: string) {
    super(items[0]?.message ?? "Ошибка сервера");
    this.name = "ApiFailure";
    this.status = status;
    this.items = items;
    this.requestId = requestId;
  }

  get code(): string {
    return this.items[0]?.code ?? "UNKNOWN";
  }
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  // Свой номер запроса: сервер пишет его в журнал и в ошибку — фронт и бэк одного запроса связаны.
  const requestId = crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", "X-Request-ID": requestId, ...(init?.headers ?? {}) },
    // Сессия живёт в httpOnly-cookie, поэтому запрос обязан её нести.
    credentials: "same-origin",
  });

  let payload: { error?: ApiErrorItem[]; results?: T } | null = null;
  try {
    payload = (await response.json()) as {
      error?: ApiErrorItem[];
      results?: T;
    };
  } catch {
    // Пока сервис перезапускается, шлюз отвечает 502 страницей HTML: это «недоступен», а не «не JSON».
    throw new ApiFailure(
      response.status,
      [
        response.status >= 500
          ? {
              code: "UNAVAILABLE",
              message: `Сервис недоступен (${response.status})`,
            }
          : { code: "BAD_RESPONSE", message: "Сервис вернул не JSON" },
      ],
      requestId,
    );
  }

  const errors = payload?.error ?? [];
  const id = response.headers.get("X-Request-ID") ?? requestId;
  if (errors.length > 0) throw new ApiFailure(response.status, errors, id);
  if (!response.ok) {
    throw new ApiFailure(
      response.status,
      [{ code: "HTTP_ERROR", message: `Сервис ответил ${response.status}` }],
      id,
    );
  }
  return payload?.results as T;
}

/** Собрать query-строку, выбросив пустые значения: они превращают ссылку в мусор. */
export function query(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === false
    )
      continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}
